import { ChangeEvent } from "react";
import { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ExpressContact } from "../../types/express-master.type";
import CustomerPicker, { CustomerOption } from "../SpecialOrder/CustomerPicker";
import MasterPicker from "../ExpressMaster/MasterPicker";
import { SEARCH_ACTIVE_CONTACTS } from "../ExpressMaster/masterSearch";
import {
  DOCUMENT_CODE_MAX_LENGTH,
  DOCUMENT_TEXT_MAX_LENGTH,
  ExpressDocTypeConfig,
} from "./constants";
import { DocumentDraft, PartyDraft } from "./documentDraft";
import { DocumentFormErrors } from "./documentRules";

interface PartyPanelProps {
  config: ExpressDocTypeConfig;
  draft: DocumentDraft;
  errors: DocumentFormErrors;
  readOnly: boolean;
  /** A saved receipt keeps its customer: its bills belong to that customer. */
  isCustomerLocked: boolean;
  /** The branch head office searches GDP customers in; undefined for a branch user. */
  customerBranchUuid?: string;
  isCustomerSearchBlocked: boolean;
  onChange: (patch: Partial<DocumentDraft>) => void;
  onContactChange: (contact: ExpressContact | null) => void;
  onCustomerChange: (customer: CustomerOption | null) => void;
  onSupplierBillDateChange: (date: Dayjs | null) => void;
}

/** The party's details under the picker, the way Express lists them. */
const partyDetailLines = (party: PartyDraft): string[] =>
  [
    party.title && `คำนำหน้า ${party.title}`,
    party.address,
    party.phone && `โทร. ${party.phone}`,
    party.taxId && `TaxID ${party.taxId}`,
    party.taxBranchNo !== null && `สาขา# ${party.taxBranchNo}`,
  ].filter((line): line is string => Boolean(line));

/** The left panel: who the document is with, and the fields of that side of the screen. */
export default function PartyPanel({
  config,
  draft,
  errors,
  readOnly,
  isCustomerLocked,
  customerBranchUuid,
  isCustomerSearchBlocked,
  onChange,
  onContactChange,
  onCustomerChange,
  onSupplierBillDateChange,
}: Readonly<PartyPanelProps>) {
  const { contactKind } = config;
  const readOnlySlot = { input: { readOnly } };
  const detailLines = draft.party ? partyDetailLines(draft.party) : [];

  const handleSupplierBillNoChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange({ supplierBillNo: event.target.value });
  const handleTaxPeriodChange = (taxPeriod: Dayjs | null) =>
    onChange({ taxPeriod: taxPeriod?.isValid() ? taxPeriod.startOf("month") : taxPeriod });
  const handleReferenceChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange({ reference: event.target.value });
  const handleSalespersonChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange({ salespersonCode: event.target.value.trim() });
  const handleRemarkChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange({ remark: event.target.value });

  let partyPicker = (
    <TextField
      fullWidth
      size="small"
      label={config.partyLabel}
      value={draft.party?.code ?? ""}
      slotProps={{ input: { readOnly: true } }}
    />
  );
  if (contactKind) {
    partyPicker = (
      <MasterPicker
        value={draft.party}
        search={SEARCH_ACTIVE_CONTACTS[contactKind]}
        onChange={onContactChange}
        label={config.partyLabel}
        placeholder="ค้นหารหัสหรือชื่อ"
        readOnly={readOnly}
        error={errors.party}
      />
    );
  } else if (!readOnly && !isCustomerLocked) {
    partyPicker = (
      <CustomerPicker
        value={draft.customer}
        onChange={onCustomerChange}
        branchUuid={customerBranchUuid}
        disabled={isCustomerSearchBlocked}
        error={errors.party}
        helperText={isCustomerSearchBlocked ? "เลือกสาขาก่อน" : undefined}
      />
    );
  }

  return (
    <Stack gap={2}>
      <Stack gap={0.5}>
        {partyPicker}
        {draft.party && (
          <Typography variant="body2" fontWeight={600}>
            {draft.party.name}
          </Typography>
        )}
        {detailLines.map((line) => (
          <Typography key={line} variant="caption" color="text.secondary">
            {line}
          </Typography>
        ))}
      </Stack>
      <Grid container spacing={2}>
        {config.hasSupplierBill && (
          <>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="เลขที่บิล"
                value={draft.supplierBillNo}
                onChange={handleSupplierBillNoChange}
                error={Boolean(errors.supplierBillNo)}
                helperText={errors.supplierBillNo}
                slotProps={{ ...readOnlySlot, htmlInput: { maxLength: DOCUMENT_CODE_MAX_LENGTH } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <DatePicker
                label="ลวท."
                value={draft.supplierBillDate}
                format="DD/MM/YYYY"
                readOnly={readOnly}
                onChange={onSupplierBillDateChange}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    error: Boolean(errors.supplierBillDate),
                    helperText: errors.supplierBillDate,
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <DatePicker
                label="ยื่นงวด"
                value={draft.taxPeriod}
                views={["year", "month"]}
                openTo="month"
                format="MM/YYYY"
                readOnly={readOnly}
                onChange={handleTaxPeriodChange}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    error: Boolean(errors.taxPeriod),
                    helperText: errors.taxPeriod,
                  },
                }}
              />
            </Grid>
          </>
        )}
        {config.lineMode === "BILLS" && (
          <>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                fullWidth
                size="small"
                label="อ้างอิง"
                value={draft.reference}
                onChange={handleReferenceChange}
                slotProps={{ ...readOnlySlot, htmlInput: { maxLength: DOCUMENT_TEXT_MAX_LENGTH } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="พนักงานขาย"
                value={draft.salespersonCode}
                onChange={handleSalespersonChange}
                slotProps={{ ...readOnlySlot, htmlInput: { maxLength: DOCUMENT_CODE_MAX_LENGTH } }}
              />
            </Grid>
          </>
        )}
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            size="small"
            label={config.remarkLabel}
            value={draft.remark}
            onChange={handleRemarkChange}
            slotProps={{ ...readOnlySlot, htmlInput: { maxLength: DOCUMENT_TEXT_MAX_LENGTH } }}
          />
        </Grid>
      </Grid>
    </Stack>
  );
}
